import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  Stack,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

const DATA_TYPES = ["Float", "Integer", "String", "Boolean", "Custom"];
const UNIT_TYPES = [
  "-",
  "dBm",
  "dB",
  "m",
  "%",
  "°C",
  "mg/kg",
  "NTU",
  "kWh",
  "L/min",
  "ppm",
  "dS/m",
  "pH",
  "V",
  "W",
  "A",
  "lx",
  "Custom",
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 3.5,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
  },
};

export default function Schema({ value = [], onChange }) {
  const rows = value.length
    ? value
    : [{ key: "", type: "", customType: "", unit: "", customUnit: "" }];

  const addRow = () => {
    onChange([
      ...rows,
      { key: "", type: "", customType: "", unit: "", customUnit: "" },
    ]);
  };

  const updateRow = (index, field, newValue) => {
    const updated = rows.map((row, i) =>
      i === index ? { ...row, [field]: newValue } : row,
    );
    onChange(updated);
  };

  const deleteRow = (index) => {
    if (rows.length === 1) return;
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <Box
      sx={{
        borderRadius: 5,
        border: "1px solid rgba(226, 232, 240, 0.95)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={1.25} sx={{ mb: 3 }}>
        <Chip
          label="Step 2"
          sx={{
            width: "fit-content",
            color: "#1D4ED8",
            backgroundColor: "rgba(59,130,246,0.12)",
            fontWeight: 700,
          }}
        />
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0F172A" }}>
          Telemetry schema
        </Typography>
        <Typography sx={{ color: "#64748B", lineHeight: 1.7 }}>
          Define the telemetry keys, data types, and units this device will
          publish so downstream analytics and monitoring stay structured.
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        {rows.map((row, index) => (
          <Box
            key={index}
            sx={{
              p: 2,
              borderRadius: 4,
              border: "1px solid rgba(226, 232, 240, 0.95)",
              backgroundColor: "#fff",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Box sx={{ minWidth: { md: 56 } }}>
                <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 800 }}>
                  ROW
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
                  {String(index + 1).padStart(2, "0")}
                </Typography>
              </Box>

              <Box sx={{ flex: 1.4 }}>
                <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 700, mb: 0.75 }}>
                  Key
                </Typography>
                <TextField
                  fullWidth
                  value={row.key}
                  onChange={(e) => updateRow(index, "key", e.target.value)}
                  placeholder="temperature, humidity, battery..."
                  sx={inputSx}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 700, mb: 0.75 }}>
                  Data Type
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={row.type}
                  onChange={(e) => updateRow(index, "type", e.target.value)}
                  sx={inputSx}
                >
                  {DATA_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {row.type === "Custom" && (
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 700, mb: 0.75 }}>
                    Custom Type
                  </Typography>
                  <TextField
                    fullWidth
                    value={row.customType}
                    onChange={(e) => updateRow(index, "customType", e.target.value)}
                    placeholder="Enter custom type"
                    sx={inputSx}
                  />
                </Box>
              )}

              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 700, mb: 0.75 }}>
                  Unit
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={row.unit}
                  onChange={(e) => updateRow(index, "unit", e.target.value)}
                  sx={inputSx}
                >
                  {UNIT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {row.unit === "Custom" && (
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 700, mb: 0.75 }}>
                    Custom Unit
                  </Typography>
                  <TextField
                    fullWidth
                    value={row.customUnit}
                    onChange={(e) => updateRow(index, "customUnit", e.target.value)}
                    placeholder="Enter custom unit"
                    sx={inputSx}
                  />
                </Box>
              )}

              <Box sx={{ alignSelf: { xs: "flex-end", md: "center" } }}>
                <IconButton
                  onClick={() => deleteRow(index)}
                  disabled={rows.length === 1}
                  sx={{
                    borderRadius: 3,
                    color: "#94A3B8",
                    "&:hover": { backgroundColor: "#FEE2E2", color: "#DC2626" },
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>

      <Button
        startIcon={<AddRoundedIcon />}
        onClick={addRow}
        sx={{
          mt: 2.5,
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 700,
          color: "#0F4C81",
          backgroundColor: "rgba(14,165,233,0.08)",
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "rgba(14,165,233,0.14)",
          },
        }}
      >
        Add telemetry row
      </Button>
    </Box>
  );
}
